import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import {
  ArrowDownCircle,
  ArrowLeftRight,
  ArrowUpCircle,
  Check,
  CheckCircle2,
  CircleAlert,
  CreditCard,
  MoreVertical,
  Pencil,
  Scale,
  Search,
  SlidersHorizontal,
  Trash2,
  Wallet,
  X,
} from 'lucide-react';
import {
  formatDateBR,
  effDay,
  formatMonthLabel,
  formatYmd,
  parseYm,
  todaySP,
  type AccountsResponse,
  type CardsResponse,
  type CreditCard as CreditCardModel,
  type RecurrenceScope,
  type Transaction,
  type TransactionsResponse,
  type Ym,
} from '@lira/shared';
import { apiDelete, apiGet, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { useMonthStore } from '@/stores/month';
import { useComposeStore } from '@/stores/compose';
import { toast } from '@/stores/toast';
import {
  CategoryIcon,
  ConfirmDialog,
  EmptyState,
  MoneyText,
  Skeleton,
} from '@/components/ui';
import { cn } from '@/lib/cn';
import { ExportCsvButton } from '@/components/ExportCsvButton';
import { PageHeader } from '@/components/layout/PageHeader';
import { FilterDrawer } from './FilterDrawer';
import { InvoiceTransactionsModal } from './InvoiceTransactionsModal';
import { RecurrenceScopeDialog } from './RecurrenceScopeDialog';
import { TransactionDetailsModal } from './TransactionDetailsModal';
import {
  hasEffectuationSource,
  runEffectuationBatch,
  transferRouteLabel,
} from './transfer-presentation';
import { formatDatePretty } from './txMeta';
import {
  EMPTY_FILTERS,
  buildTransactionsQuery,
  buildTransactionsSearchBody,
  countActiveFilters,
  filtersFromSearchParams,
  searchFromFilters,
  type TxFilters,
} from './filters';

const EASE = [0.16, 1, 0.3, 1] as const;
const VALID_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Chave da memória de filtro por sessão (subconjunto compartilhável, serializado como query string). */
const TX_FILTERS_KEY = 'lira:txFilters';

type InvoiceRowGroup = {
  id: string;
  card: CreditCardModel;
  invoiceMonth: Ym;
  dueDate: string;
  rows: Transaction[];
  totalCents: number;
};

// --- Cards-resumo (hero) ---------------------------------------------------

function HeroCard({
  label,
  cents,
  icon: Icon,
  tint,
  onClick,
}: {
  label: string;
  cents: number;
  icon: typeof Wallet;
  tint: string;
  onClick?: () => void;
}) {
  // Ícone+rótulo em cima e o valor numa linha própria de largura total: no grid
  // de 2 colunas do mobile, R$ grandes não cabiam ao lado do ícone e vazavam
  // para o card vizinho. Layout único, robusto em qualquer largura.
  return (
    <button type="button" onClick={onClick} className={cn('rounded-[var(--radius-panel)] bg-surface p-4 text-left shadow-card transition-shadow hover:shadow-hover', onClick && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary')}>
      <div className="flex items-center gap-2">
        <span className={cn('grid size-9 shrink-0 place-items-center rounded-full text-white', tint)}>
          <Icon className="size-[18px]" />
        </span>
        <p className="min-w-0 flex-1 truncate text-[13px] text-muted">{label}</p>
      </div>
      <MoneyText cents={cents} bold className="mt-1.5 block truncate text-lg" />
    </button>
  );
}

// --- Pílula de tipo --------------------------------------------------------

const TYPE_PILLS: { value: TxFilters['type']; label: string; dot: string }[] = [
  { value: null, label: 'Todas', dot: 'bg-primary' },
  { value: 'expense', label: 'Despesas', dot: 'bg-expense' },
  { value: 'income', label: 'Receitas', dot: 'bg-income' },
  { value: 'transfer', label: 'Transferências', dot: 'bg-balance' },
];

// --- Situação / categoria --------------------------------------------------

function SituationBadge({ row }: { row: Transaction }) {
  if (row.cardId) {
    return (
      <span title="Lançamento no cartão" className="grid size-6 place-items-center text-cards">
        <CreditCard className="size-4" />
      </span>
    );
  }
  if (row.effectuated) {
    return (
      <span title="Efetivada" className="grid size-6 place-items-center text-income">
        <CheckCircle2 className="size-4" />
      </span>
    );
  }
  return (
    <span title="Pendente" className="grid size-6 place-items-center text-expense">
      <CircleAlert className="size-4" />
    </span>
  );
}

function CategoryCell({ row }: { row: Transaction }) {
  if (row.type === 'transfer') {
    return (
      <span className="flex items-center gap-2">
        <span className="grid size-7 place-items-center rounded-full bg-balance/10 text-balance">
          <ArrowLeftRight className="size-3.5" />
        </span>
        <span className="truncate text-ink">Transferência</span>
        <span
          className="shrink-0 rounded bg-balance px-1.5 py-0.5 text-[10px] font-bold leading-none text-white"
          title="Transferência (categoria de sistema)"
        >
          TR
        </span>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2">
      <CategoryIcon icon={row.categoryIcon} color={row.categoryColor ?? 'var(--color-muted)'} size={28} />
      <span className="truncate text-ink">
        {row.categoryName ?? 'Sem categoria'}
        {row.categoryIsSystem && '*'}
      </span>
    </span>
  );
}

// --- Menu por linha --------------------------------------------------------

function RowMenu({
  row,
  onEdit,
  onEffectuate,
  onDelete,
}: {
  row: Transaction;
  onEdit: () => void;
  onEffectuate: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top?: number; bottom?: number; right: number }>({ right: 8 });

  const updatePosition = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const itemCount = !row.effectuated && !row.cardId ? 3 : 2;
    const estimatedMenuHeight = itemCount * 40 + 10;
    const opensAbove = rect.bottom + estimatedMenuHeight + 8 > window.innerHeight;
    setPosition({
      right: Math.max(8, window.innerWidth - rect.right),
      ...(opensAbove
        ? { bottom: Math.max(8, window.innerHeight - rect.top + 4) }
        : { top: rect.bottom + 4 }),
    });
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  const act = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => {
          if (!open) updatePosition();
          setOpen((v) => !v);
        }}
        aria-label="Ações"
        aria-haspopup="menu"
        aria-expanded={open}
        className="grid size-8 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink"
      >
        <MoreVertical className="size-5" />
      </button>
      {createPortal(
        <>
          {open && <div className="fixed inset-0 z-50" aria-hidden onClick={() => setOpen(false)} />}
          <AnimatePresence>
            {open && (
              <m.div
                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.98 }}
                transition={{ duration: 0.15, ease: EASE }}
                role="menu"
                style={{ ...position, backgroundColor: 'var(--color-surface)' }}
                className="fixed z-[60] min-w-44 overflow-hidden rounded-lg border border-border bg-surface! py-1 shadow-hover ring-1 ring-black/5 dark:ring-white/10"
              >
                <MenuItem icon={Pencil} label="Editar" onClick={act(onEdit)} />
                {!row.effectuated && !row.cardId && (
                  <MenuItem icon={Check} label="Efetivar" onClick={act(onEffectuate)} />
                )}
                <MenuItem icon={Trash2} label="Excluir" danger onClick={act(onDelete)} />
              </m.div>
            )}
          </AnimatePresence>
        </>,
        document.body,
      )}
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: typeof Pencil;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-canvas',
        danger ? 'text-expense' : 'text-ink',
      )}
    >
      <Icon className={cn('size-4 shrink-0', danger ? 'text-expense' : 'text-muted')} />
      {label}
    </button>
  );
}

// --- Checkbox --------------------------------------------------------------

function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={cn(
        'grid size-5 shrink-0 place-items-center rounded border transition-colors',
        checked ? 'border-primary bg-primary text-white' : 'border-border bg-surface hover:border-muted',
      )}
    >
      {checked && <Check className="size-3.5" strokeWidth={3} />}
    </button>
  );
}

// --- Página ----------------------------------------------------------------

export function TransactionsPage() {
  const month = useMonthStore((s) => s.month);
  const setMonth = useMonthStore((s) => s.setMonth);
  const direction = useMonthStore((s) => s.direction);
  const reduce = useReducedMotion();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const openTypeSheet = useComposeStore((s) => s.openTypeSheet);
  const openEdit = useComposeStore((s) => s.openEdit);
  const [searchParams, setSearchParams] = useSearchParams();

  // Semente inicial dos filtros: a URL manda (deep-links de KPI/conta/relatório,
  // bookmark, reload). Sem query, restaura o último filtro da sessão — atende
  // "lembrar o último filtro por sessão" (voltar a Transações pelo rail cai em
  // /transacoes sem query). Roda uma única vez na montagem.
  const [seed] = useState<Partial<TxFilters>>(() => {
    const fromUrl = filtersFromSearchParams(searchParams);
    if (Object.keys(fromUrl).length > 0) return fromUrl;
    try {
      return filtersFromSearchParams(new URLSearchParams(sessionStorage.getItem(TX_FILTERS_KEY) ?? ''));
    } catch {
      return {};
    }
  });
  const [filters, setFilters] = useState<TxFilters>(() => ({ ...EMPTY_FILTERS, ...seed }));
  const [searchText, setSearchText] = useState(seed.search ?? '');
  const [searchOpen, setSearchOpen] = useState(!!seed.search);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [groupCards, setGroupCards] = useState(() => {
    if (searchParams.get('groupCards') === '1') return true;
    try {
      return new URLSearchParams(sessionStorage.getItem(TX_FILTERS_KEY) ?? '').get('groupCards') === '1';
    } catch {
      return false;
    }
  });
  const [groupBy, setGroupBy] = useState<'date' | 'card' | 'category'>('date');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [deleting, setDeleting] = useState<Transaction | null>(null);
  const [details, setDetails] = useState<Transaction | null>(null);
  const [invoiceSelection, setInvoiceSelection] = useState<{ card: CreditCardModel; month: Ym } | null>(null);
  const monthSync = useRef<{ initialized: boolean; urlMonth: string | null; storeMonth: string }>({
    initialized: false,
    urlMonth: null,
    storeMonth: month,
  });

  useEffect(() => {
    const requestedMonth = searchParams.get('month');
    const validUrlMonth = requestedMonth && VALID_MONTH.test(requestedMonth) ? requestedMonth : null;
    const previous = monthSync.current;

    if (!previous.initialized) {
      monthSync.current = { initialized: true, urlMonth: validUrlMonth, storeMonth: month };
      if (validUrlMonth && validUrlMonth !== month) setMonth(validUrlMonth);
      return;
    }

    const urlChanged = validUrlMonth !== previous.urlMonth;
    const storeChanged = month !== previous.storeMonth;
    monthSync.current = { initialized: true, urlMonth: validUrlMonth, storeMonth: month };
    if (urlChanged && validUrlMonth && validUrlMonth !== month) {
      setMonth(validUrlMonth);
      return;
    }
    if (!urlChanged && storeChanged) return;
  }, [month, searchParams, setMonth]);

  // Sincroniza o subconjunto compartilhável dos filtros com a URL (bookmarkable)
  // e com a memória de sessão. replace: não polui o histórico do navegador.
  useEffect(() => {
    const requestedMonth = searchParams.get('month');
    if (requestedMonth && VALID_MONTH.test(requestedMonth) && requestedMonth !== month) return;
    const qs = new URLSearchParams(searchFromFilters(filters));
    qs.set('month', month);
    if (groupCards) qs.set('groupCards', '1');
    setSearchParams(qs, { replace: true });
    try {
      sessionStorage.setItem(TX_FILTERS_KEY, qs.toString());
    } catch {
      /* modo privado pode bloquear o storage — ignora */
    }
  }, [
    filters.type,
    filters.status,
    filters.accountIds,
    filters.categoryIds,
    filters.tagIds,
    filters.search,
    groupCards,
    month,
    setSearchParams,
  ]);

  // debounce da busca
  useEffect(() => {
    const t = window.setTimeout(() => setFilters((f) => ({ ...f, search: searchText })), 300);
    return () => window.clearTimeout(t);
  }, [searchText]);

  // limpa seleção ao trocar de mês ou representação de cartões
  useEffect(() => setSelected(new Set()), [month, groupCards]);

  const qs = useMemo(() => buildTransactionsQuery(month, filters), [month, filters]);
  const query = useQuery({
    queryKey: ['transactions', qs, filters.search],
    queryFn: () => apiPost<TransactionsResponse>('/transactions/search', buildTransactionsSearchBody(month, filters)),
  });
  // Os quatro KPIs formam um resumo único do mês selecionado. A lista pode ser
  // refinada por tipo/status/conta/categoria/busca sem alterar a leitura mensal.
  const monthlySummaryQuery = useQuery({
    queryKey: ['transactions', 'monthly-summary', month],
    queryFn: () => apiGet<TransactionsResponse>(`/transactions?month=${month}&view=summary`),
  });
  const accountsQuery = useQuery({
    queryKey: ['accounts', 'compose'],
    queryFn: () => apiGet<AccountsResponse>('/accounts'),
  });
  const cardsQuery = useQuery({
    queryKey: ['cards', month],
    queryFn: () => apiGet<CardsResponse>(`/cards?month=${month}`),
    enabled: groupCards,
  });

  const summary = monthlySummaryQuery.data?.summary;
  const rows = useMemo(() => {
    return [...(query.data?.rows ?? [])].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  }, [query.data]);
  const canGroupInvoices = groupCards
    && filters.type !== 'income'
    && filters.type !== 'transfer'
    && filters.status !== 'pending'
    && filters.categoryIds.length === 0
    && filters.accountIds.length === 0
    && filters.tagIds.length === 0
    && !filters.from
    && !filters.to;
  const invoiceGroups = useMemo<InvoiceRowGroup[]>(() => {
    if (!canGroupInvoices) return [];
    const cardsById = new Map((cardsQuery.data?.cards ?? []).map((card) => [card.id, card]));
    const grouped = new Map<string, InvoiceRowGroup>();
    for (const row of rows) {
      if (row.kind !== 'standard' || row.ignoreInReports || !row.cardId || !row.invoiceMonth) continue;
      const card = cardsById.get(row.cardId);
      if (!card) continue;
      const key = `${row.cardId}:${row.invoiceMonth}`;
      const parsed = parseYm(row.invoiceMonth);
      const group = grouped.get(key) ?? {
        id: key,
        card,
        invoiceMonth: row.invoiceMonth,
        dueDate: formatYmd(parsed.year, parsed.month, effDay(parsed.year, parsed.month, card.dueDay)),
        rows: [],
        totalCents: 0,
      };
      group.rows.push(row);
      group.totalCents += row.amountCents;
      grouped.set(key, group);
    }
    return [...grouped.values()].sort((a, b) => b.dueDate.localeCompare(a.dueDate));
  }, [canGroupInvoices, cardsQuery.data, rows]);
  const groupedRowIds = useMemo(
    () => new Set(invoiceGroups.flatMap((group) => group.rows.map((row) => row.id))),
    [invoiceGroups],
  );
  const visibleRows = useMemo(
    () => rows.filter((row) => !groupedRowIds.has(row.id)),
    [groupedRowIds, rows],
  );

  const groups = useMemo(() => {
    const map = new Map<string, Transaction[]>();
    for (const r of visibleRows) {
      const source = r.type === 'transfer'
        ? transferRouteLabel(r) ?? 'Transferência'
        : r.cardName ?? r.accountName ?? 'Sem cartão/conta';
      const key = groupBy === 'card' ? source : groupBy === 'category' ? (r.categoryName ?? 'Sem categoria') : r.date;
      const arr = map.get(key) ?? [];
      arr.push(r);
      map.set(key, arr);
    }
    return [...map.entries()];
  }, [visibleRows, groupBy]);

  const activeCount = countActiveFilters(filters);
  const allSelected = visibleRows.length > 0 && visibleRows.every((r) => selected.has(r.id));

  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(visibleRows.map((r) => r.id)));

  // --- mutations ---
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['expense-calendar'] });
  };

  const effectuate = useMutation({
    mutationFn: (id: string) => apiPost(`/transactions/${id}/effectuate`),
    onSuccess: () => {
      invalidate();
      toast('Lançamento efetivado.', 'success');
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const deleteOne = useMutation({
    mutationFn: (vars: { id: string; scope: RecurrenceScope }) =>
      apiDelete(`/transactions/${vars.id}?scope=${vars.scope}`),
    onSuccess: () => {
      invalidate();
      toast('Lançamento excluído.', 'success');
      setDeleting(null);
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const batchDelete = useMutation({
    mutationFn: (ids: string[]) =>
      Promise.all(ids.map((id) => apiDelete(`/transactions/${id}?scope=one`))),
    onSuccess: () => {
      invalidate();
      toast('Selecionadas excluídas.', 'success');
      setSelected(new Set());
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const batchEffectuate = useMutation({
    mutationFn: (ids: string[]) =>
      runEffectuationBatch(ids, (id) => apiPost(`/transactions/${id}/effectuate`)),
    onSuccess: () => {
      toast('Selecionadas efetivadas.', 'success');
      setSelected(new Set());
    },
    onError: (err) => toast(errorMessage(err), 'error'),
    onSettled: invalidate,
  });

  const requestDelete = (row: Transaction) => setDeleting(row);
  const confirmDelete = (scope: RecurrenceScope) => {
    if (deleting) deleteOne.mutate({ id: deleting.id, scope });
  };

  const selectedIds = [...selected];
  const pendingSelected = visibleRows
    .filter((r) => selected.has(r.id) && !r.effectuated && !r.cardId && hasEffectuationSource(r) && r.date <= todaySP())
    .map((r) => r.id);

  // --- render ---
  const showEmpty = query.isSuccess
    && (!groupCards || cardsQuery.isSuccess)
    && visibleRows.length === 0
    && invoiceGroups.length === 0;

  return (
    <section className="space-y-5">
      <PageHeader
        title="Transações"
        subtitle="Consulte, filtre e gerencie seus lançamentos."
      />

      {/* hero cards */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <HeroCard
          label="Saldo atual"
          cents={accountsQuery.data?.totalCurrentCents ?? 0}
          icon={Wallet}
          tint="bg-balance"
          onClick={() => navigate('/contas')}
        />
        <HeroCard label="Receitas" cents={summary?.incomeCents ?? 0} icon={ArrowUpCircle} tint="bg-income" onClick={() => setFilters((f) => ({ ...f, type: 'income' }))} />
        <HeroCard
          label="Despesas"
          cents={summary?.expenseCents ?? 0}
          icon={ArrowDownCircle}
          tint="bg-expense"
          onClick={() => setFilters((f) => ({ ...f, type: 'expense' }))}
        />
        <HeroCard
          label="Balanço mensal"
          cents={summary?.balanceCents ?? 0}
          icon={Scale}
          tint="bg-primary"
          onClick={() => navigate('/relatorios')}
        />
      </div>

      {/* barra de filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-2">
          {TYPE_PILLS.map((pill) => {
            const active = filters.type === pill.value;
            return (
              <button
                key={pill.label}
                type="button"
                onClick={() => setFilters((f) => ({ ...f, type: pill.value }))}
                className={cn(
                  'flex items-center gap-2 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors',
                  active
                    ? 'border-primary bg-primary-weak text-primary'
                    : 'border-border text-muted hover:border-muted hover:text-ink',
                )}
              >
                <span className={cn('size-2 rounded-full', pill.dot)} />
                {pill.label}
              </button>
            );
          })}
        </div>

        <div className="flex w-full min-w-0 items-center justify-end gap-2 sm:ml-auto sm:w-auto">
          <label className="sr-only" htmlFor="transactions-group-by">Agrupar lançamentos</label>
          <select id="transactions-group-by" value={groupBy} onChange={(e) => setGroupBy(e.target.value as 'date' | 'card' | 'category')} className="h-9 min-w-0 flex-1 rounded-full border border-border bg-surface px-3 text-sm text-muted focus:border-primary focus:outline-none sm:flex-none"><option value="date">Por data</option><option value="card">Por cartão</option><option value="category">Por categoria</option></select>
          <AnimatePresence initial={false}>
            {searchOpen && (
              <m.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 200, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: EASE }}
                className="overflow-hidden"
              >
                <input
                  autoFocus
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Nome, valor, data…"
                  aria-label="Buscar lançamentos"
                  title="Busca por nome, valor, data, categoria ou conta. Regex: /padrão/i"
                  className="h-9 w-[200px] rounded-full border border-field bg-surface px-3 text-sm text-ink focus:border-primary focus:outline-none"
                />
              </m.div>
            )}
          </AnimatePresence>
          <button
            type="button"
            onClick={() => {
              if (searchOpen) setSearchText('');
              setSearchOpen((v) => !v);
            }}
            aria-label="Buscar"
            className={cn(
              'grid size-9 place-items-center rounded-full border transition-colors',
              searchOpen
                ? 'border-primary text-primary'
                : 'border-border text-muted hover:border-muted hover:text-ink',
            )}
          >
            {searchOpen ? <X className="size-4" /> : <Search className="size-4" />}
          </button>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Filtrar transações"
            className={cn(
              'grid size-9 shrink-0 place-items-center rounded-full border text-sm font-medium transition-colors min-[360px]:flex min-[360px]:w-auto min-[360px]:gap-2 min-[360px]:px-3',
              activeCount > 0
                ? 'border-primary text-primary'
                : 'border-border text-muted hover:border-muted hover:text-ink',
            )}
          >
            <SlidersHorizontal className="size-4" />
            <span className="hidden min-[360px]:inline">Filtrar</span>
            {activeCount > 0 && (
              <span className="grid size-5 place-items-center rounded-full bg-primary text-[11px] font-bold text-white">
                {activeCount}
              </span>
            )}
          </button>
          <ExportCsvButton
            qs={qs}
            filename={`transacoes-${month}.csv`}
            variant="icon"
          />
        </div>
      </div>

      {/* conteúdo animado por mês — a key re-dispara o slide de entrada a cada
          troca de mês (sem exit, para não depender de AnimatePresence e evitar
          que o div do mês anterior fique preso na animação de saída). */}
      <div className="relative overflow-x-hidden">
        <m.div
          key={month}
          initial={reduce ? { opacity: 0 } : { opacity: 0, x: direction * 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2, ease: EASE }}
        >
          {query.isLoading && (
            <div className="space-y-2 rounded-[var(--radius-panel)] bg-surface p-4 shadow-card">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}

          {query.isError && (
            <p className="rounded-lg bg-expense-weak px-4 py-6 text-center text-sm text-expense">
              {errorMessage(query.error)}
            </p>
          )}

          {showEmpty && (
            <EmptyState
              icon={ArrowLeftRight}
              title="Nenhuma transação neste mês"
              description="Registre despesas, receitas e transferências para acompanhar seu mês."
            >
              <button
                type="button"
                onClick={(e) => openTypeSheet(e.currentTarget.getBoundingClientRect())}
                className="inline-flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover"
              >
                Novo lançamento
              </button>
            </EmptyState>
          )}

          {query.isSuccess && (visibleRows.length > 0 || invoiceGroups.length > 0) && (
            <>
              {/* Desktop: tabela */}
              <div className="hidden overflow-hidden rounded-[var(--radius-panel)] bg-surface shadow-card lg:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[12px] uppercase tracking-wide text-muted">
                      <th className="w-10 py-2.5 pl-4">
                        <Checkbox checked={allSelected} onChange={toggleAll} label="Selecionar todas" />
                      </th>
                      <th className="w-12 py-2.5 text-center font-semibold">Sit.</th>
                      <th className="py-2.5 font-semibold">Data</th>
                      <th className="py-2.5 font-semibold">Descrição</th>
                      <th className="py-2.5 font-semibold">Categoria</th>
                      <th className="py-2.5 font-semibold">Conta</th>
                      <th className="py-2.5 text-right font-semibold">Valor</th>
                      <th className="w-12 py-2.5 pr-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => (
                      <tr
                        key={row.id}
                        className={cn(
                          'border-b border-border last:border-0 transition-colors hover:bg-canvas/60',
                          selected.has(row.id) && 'bg-primary-weak/40',
                        )}
                      >
                        <td className="py-2.5 pl-4">
                          <Checkbox
                            checked={selected.has(row.id)}
                            onChange={() => toggleSelected(row.id)}
                            label={`Selecionar ${row.description || 'lançamento'}`}
                          />
                        </td>
                        <td className="py-2.5">
                          <div className="grid place-items-center">
                            <SituationBadge row={row} />
                          </div>
                        </td>
                        <td className="whitespace-nowrap py-2.5 tabular-nums text-muted">
                          {formatDateBR(row.date)}
                        </td>
                        <td className="max-w-[220px] py-2.5">
                          <button
                            type="button"
                            aria-label={`Ver detalhes de ${row.description || 'lançamento'}`}
                            onClick={() => setDetails(row)}
                            className="block max-w-full truncate rounded px-1 py-1 text-left font-medium text-ink hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          >
                            {row.description || '—'}
                            {row.installmentTotal && (
                              <span className="ml-1 text-[12px] text-muted">
                                {row.installmentIndex}/{row.installmentTotal}
                              </span>
                            )}
                          </button>
                        </td>
                        <td className="max-w-[180px] py-2.5">
                          <CategoryCell row={row} />
                        </td>
                        <td className="max-w-[140px] py-2.5">
                          <span className="block truncate text-muted">
                            {row.type === 'transfer'
                                ? transferRouteLabel(row) ?? 'Transferência'
                                : row.accountName ?? row.cardName ?? '—'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap py-2.5 pr-1 text-right">
                          <MoneyText cents={row.amountCents} colorBySign={row.type !== 'transfer'} showPositiveSign={row.type === 'income'} bold />
                        </td>
                        <td className="py-2.5 pr-2">
                          <RowMenu
                            row={row}
                            onEdit={() => openEdit(row)}
                            onEffectuate={() => effectuate.mutate(row.id)}
                            onDelete={() => requestDelete(row)}
                          />
                        </td>
                      </tr>
                    ))}
                    {invoiceGroups.map((group) => (
                      <tr key={`invoice-${group.id}`} className="border-b border-border bg-cards/5 last:border-0">
                        <td className="py-2.5 pl-4" />
                        <td className="py-2.5"><span className="grid place-items-center text-cards"><CreditCard className="size-4" /></span></td>
                        <td className="whitespace-nowrap py-2.5 tabular-nums text-muted">{formatDateBR(group.dueDate)}</td>
                        <td className="max-w-[220px] py-2.5">
                          <button
                            type="button"
                            aria-label={`Ver fatura ${group.card.name} de ${formatMonthLabel(group.invoiceMonth)}`}
                            onClick={() => setInvoiceSelection({ card: group.card, month: group.invoiceMonth })}
                            className="flex max-w-full flex-col rounded px-1 py-1 text-left font-semibold text-cards hover:bg-cards/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cards"
                          >
                            <span className="max-w-full truncate">Fatura {group.card.name} · {formatMonthLabel(group.invoiceMonth)}</span>
                            <MoneyText cents={group.totalCents} colorBySign bold className="text-xs" />
                          </button>
                        </td>
                        <td className="py-2.5 text-muted">{group.rows.length} compra{group.rows.length === 1 ? '' : 's'} agrupada{group.rows.length === 1 ? '' : 's'}</td>
                        <td className="max-w-[140px] py-2.5"><span className="block truncate text-muted">{group.card.name}</span></td>
                        <td className="whitespace-nowrap py-2.5 pr-1 text-right"><MoneyText cents={group.totalCents} colorBySign bold /></td>
                        <td className="py-2.5 pr-2" />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile: lista agrupada por dia */}
              <div className="space-y-4 lg:hidden">
                {groups.map(([key, dayRows]) => (
                  <div key={key}>
                    <p className="mb-1 px-1 text-[12px] font-semibold uppercase tracking-wide text-muted">
                      {groupBy === 'date' ? formatDatePretty(key) : key}
                      <span className="ml-2 normal-case text-muted/80">{dayRows.length} lançamento{dayRows.length === 1 ? '' : 's'}</span>
                    </p>
                    <div className="overflow-hidden rounded-[var(--radius-panel)] bg-surface shadow-card">
                      {dayRows.map((row) => (
                        <div
                          key={row.id}
                          className={cn(
                            'flex items-center gap-2.5 border-b border-border px-3 last:border-0',
                            selected.has(row.id) && 'bg-primary-weak/40',
                          )}
                        >
                          <Checkbox
                            checked={selected.has(row.id)}
                            onChange={() => toggleSelected(row.id)}
                            label={`Selecionar ${row.description || 'lançamento'}`}
                          />
                          {/* Corpo tocável abre detalhes; edição/exclusão continuam no menu de ações. */}
                          <button
                            type="button"
                            aria-label={`Ver detalhes de ${row.description || row.categoryName || 'lançamento'}`}
                            onClick={() => setDetails(row)}
                            className="flex min-w-0 flex-1 items-center gap-2.5 py-2.5 text-left"
                          >
                            {row.type === 'transfer' ? (
                              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-balance/10 text-balance">
                                <ArrowLeftRight className="size-4" />
                              </span>
                            ) : (
                              <CategoryIcon
                                icon={row.categoryIcon}
                                color={row.categoryColor ?? 'var(--color-muted)'}
                                size={36}
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="flex items-center gap-1.5 truncate font-medium text-ink">
                                {row.description || row.categoryName || 'Transferência'}
                                {!row.effectuated && !row.cardId && (
                                  <CircleAlert className="size-3.5 shrink-0 text-expense" />
                                )}
                              </p>
                              <p className="truncate text-[13px] text-muted">
                                {row.type === 'transfer'
                                  ? 'Transferência'
                                  : `${row.categoryName ?? 'Sem categoria'}${row.categoryIsSystem ? '*' : ''}`}
                                {' · '}
                                {row.type === 'transfer'
                                ? transferRouteLabel(row) ?? 'Transferência'
                                : row.accountName ?? row.cardName ?? '—'}
                              </p>
                            </div>
                            <MoneyText cents={row.amountCents} colorBySign={row.type !== 'transfer'} showPositiveSign={row.type === 'income'} bold className="shrink-0" />
                          </button>
                          <RowMenu
                            row={row}
                            onEdit={() => openEdit(row)}
                            onEffectuate={() => effectuate.mutate(row.id)}
                            onDelete={() => requestDelete(row)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {invoiceGroups.length > 0 && (
                  <div>
                    <p className="mb-1 px-1 text-[12px] font-semibold uppercase tracking-wide text-muted">Faturas de cartão</p>
                    <div className="overflow-hidden rounded-[var(--radius-panel)] bg-surface shadow-card">
                      {invoiceGroups.map((group) => (
                        <button
                          key={group.id}
                          type="button"
                          aria-label={`Ver fatura ${group.card.name} de ${formatMonthLabel(group.invoiceMonth)}`}
                          onClick={() => setInvoiceSelection({ card: group.card, month: group.invoiceMonth })}
                          className="flex w-full min-w-0 items-center gap-3 border-b border-border px-3 py-3 text-left last:border-0 hover:bg-cards/5"
                        >
                          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-cards/10 text-cards"><CreditCard className="size-4" /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-semibold text-ink">Fatura {group.card.name} · {formatMonthLabel(group.invoiceMonth)}</span>
                            <span className="block truncate text-xs text-muted">{group.rows.length} compra{group.rows.length === 1 ? '' : 's'} · vence em {formatDateBR(group.dueDate)}</span>
                          </span>
                          <MoneyText cents={group.totalCents} colorBySign bold className="shrink-0 text-sm" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </m.div>
      </div>

      {/* barra de ações em lote */}
      <AnimatePresence>
        {selected.size > 0 && (
          <m.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="fixed inset-x-0 bottom-20 z-40 mx-auto flex w-[min(92%,560px)] items-center gap-3 rounded-full border border-white/10 bg-[#22252b] px-4 py-2.5 text-white shadow-hover lg:bottom-6"
          >
            <span className="text-sm font-semibold">{selected.size} selecionada(s)</span>
            <div className="ml-auto flex items-center gap-2">
              {pendingSelected.length > 0 && (
                <button
                  type="button"
                  onClick={() => batchEffectuate.mutate(pendingSelected)}
                  disabled={batchEffectuate.isPending}
                  className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium transition-colors hover:bg-white/20 disabled:opacity-60"
                >
                  <Check className="size-4" />
                  Efetivar
                </button>
              )}
              <button
                type="button"
                onClick={() => batchDelete.mutate(selectedIds)}
                disabled={batchDelete.isPending}
                className="flex items-center gap-1.5 rounded-full bg-expense px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                <Trash2 className="size-4" />
                Excluir
              </button>
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                aria-label="Limpar seleção"
                className="grid size-8 place-items-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="size-4" />
              </button>
            </div>
          </m.div>
        )}
      </AnimatePresence>

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        value={filters}
        month={month}
        onApply={setFilters}
        groupCards={groupCards}
        onGroupCardsChange={setGroupCards}
      />

      <TransactionDetailsModal
        open={!!details}
        transactionId={details?.id ?? null}
        initialTransaction={details}
        onClose={() => setDetails(null)}
      />
      <InvoiceTransactionsModal
        open={!!invoiceSelection}
        card={invoiceSelection?.card ?? null}
        month={invoiceSelection?.month ?? month}
        onClose={() => setInvoiceSelection(null)}
      />

      {/* exclusão: recorrente → escopo; simples → confirmação */}
      {deleting?.recurrenceId ? (
        <RecurrenceScopeDialog
          open={!!deleting}
          title="Excluir lançamento recorrente"
          confirmLabel="Excluir"
          danger
          loading={deleteOne.isPending}
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      ) : (
        <ConfirmDialog
          open={!!deleting}
          title="Excluir lançamento"
          message="Esta ação não pode ser desfeita."
          loading={deleteOne.isPending}
          onConfirm={() => confirmDelete('one')}
          onCancel={() => setDeleting(null)}
        />
      )}
    </section>
  );
}
